import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "@layouts/dashboard/dashboard-layout";
import { getValue, setValue } from "@utils/lodash";
import { useNavigate } from "react-router-dom";
import NoAccessPage from "@components/common/NoAccess";
import RightMenuHeaders from "@components/common/RightMenuHeaders";
import { QueryRequestHelper } from "@_common/query-request-helper";
import { documentLibrary, legalUpdate } from "@services/reports.service";
import Loader from "@components/common/Loader";
import { concatString, decodeBase64 } from "@_common/text-helpers";
import { convertCurrentDate, convertCurrentDateWithTime, convertDate, convertDateAsString } from "@_common/date-helpers";
import {
  adminGetSCCountriesListManagement,
  adminGetSCStatesListManagement,
  getSmartFilterListManagementList,
} from "@services/list-management.service";
import { toast } from "react-toastify";
import FooterWrapper from "@components/common/FooterWrapper";
import Notfound from "@components/Not-Found";
import MenuRepositorySvgComponent from "../../assets/svg-components/left-menu/menurepo";
import ScrollableDataTable from "@components/ScrollableDatatable/index";
import { tableStatusStyle } from "@_common/colors";
import { formatText } from "@_common/format-text";
import { handleDownloadOnClick } from "@_common/downloadfile";
import DocumentLibraryPopup from "./document-library-popup";
import PreviousDocumentLibraryPopup from "./prev-document-library-popup";

const DocumentLibrary = (props: any) => {
    let navigate = useNavigate();
  /* -------------------------------------------------------------------------- */
  /*                               UseState Section                             */
  /* -------------------------------------------------------------------------- */

  const [isLoading, setIsLoading] = useState(true);
  const [documentLibraryList, setDocumentLibraryList] = useState([]);
  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const handleFilter = () => {
    setFilterIsOpen(!filterIsOpen);
  };

  /* -------------------------------------------------------------------------- */
  /*                               UseEffect Section                            */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length === 0 && props.permissionAPITriggered) {
      getData();
      let dates = params.date ? new Date(params.date) : new Date();
      var from_date = new Date(dates.getFullYear(), dates.getMonth(), 1);
      var to_date = new Date(dates.getFullYear(), dates.getMonth() + 1, 0);
      setFirstDay(from_date);
      setLastDay(to_date);
    }
  }, [props.permissionAPITriggered]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length > 0 && props.permissionAPITriggered) {
      getData();

      if (params.page_no) {
        setPage_no(parseInt(params.page_no));
      }
      if (params.search_text) {
        setSearchInput(params.search_text);
        setShowSearchTextbox(true);
      }
      if (params.limit) {
        setLimit(parseInt(params.limit));
      }

      if (params.from_date && params.to_date) {
        // setDate(new Date(params.date));
        var from_date = new Date(params.from_date);
        var to_date = new Date(params.to_date);
        setFirstDay(from_date);
        setLastDay(to_date);
      }
    }
  }, [window.location.href, props.permissionAPITriggered]);

  /* -------------------------------------------------------------------------- */
  /*                               API Section                                  */
  /* -------------------------------------------------------------------------- */
  const getPayload = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    let dates = params.date ? new Date(params.date) : new Date();
    var from_date = params.from_date
      ? params.from_date
      : new Date(dates.getFullYear(), dates.getMonth(), 1);
    var to_date = params.to_date
      ? params.to_date
      : new Date(dates.getFullYear(), dates.getMonth() + 1, 0);
    let payload: any = {
      from_date: getValue(params, `is_all_time`, "")
        ? ""
        : getValue(params, `from_date`, "")
        ? getValue(params, `from_date`, "")
        : convertDate(from_date),
      to_date: getValue(params, `is_all_time`, "")
        ? ""
        : getValue(params, `to_date`, "")
        ? getValue(params, `to_date`, "")
        : convertDate(to_date),
      page_no: params.page_no ? parseInt(params.page_no) : 1,
      page_size: params.limit ? parseInt(params.limit) : limit,
      sort_by: params.sort_by,
      search_text: params.search_text,
      country_id: parseInt(getValue(params, `country_id`, "")),
    };
    return { ...payload };
  };

  const getData = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let user: any = JSON.parse(getValue(localStorage, `user`, ""));

    let payload: any = {
      page_no: params.page_no ? parseInt(params.page_no) : 1,
      page_size: params.limit ? parseInt(params.limit) : limit,
      search_text: params.search_text,
      sort_by: params.sort_by,
      organizationId: getValue(user, `org_id`, ""),
    };

    try {
      setIsLoading(true);
      let resp = await documentLibrary(payload);
      if (resp) {
        setDocumentLibraryList(
            getValue(resp, 'data.document', []).map((item: object) => {
                const documents = getValue(item, 'documents', []);
                const previousDocumentNames = documents.slice(1).map((doc: any) => 
                    getValue(doc, 'name', '') + getValue(doc, 'extension', '')
                ).join('\n');
        
                return {
                    ...item,
                    created_at: convertCurrentDate(getValue(item, 'updated_at', '')),
                    current_document: getValue(item,`documents[${0}].name`, "") + getValue(item,`documents[${0}].extension`, ""),
                    previous_document: previousDocumentNames
                };
            })
        );
        
        setTotalCount(getValue(resp, `data.pagination.total`, 0));
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               Pagination section                           */
  /* -------------------------------------------------------------------------- */

  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(100);
  const [page_no, setPage_no] = useState(1);

  const handleChangePagination = (page_no: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload = {
      ...params,
      page_no: page_no,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  const handleChangeLimit = (limit: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...params,
      limit: limit,
      page_no: "1",
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  /* -------------------------------------------------------------------------- */
  /*                               Search section                               */
  /* -------------------------------------------------------------------------- */
  const [showSearchTextbox, setShowSearchTextbox] = useState(false);
  const [search_text, setSearchInput] = useState<any>("");

  const toggleSearch = () => {
    setShowSearchTextbox((prevState) => !prevState);
  };
  const toggleSearchClose = () => {
    setSearchInput("");
    toggleSearch();
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (params.search_text) {
      delete params.search_text;
      let payload: any = { ...params };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
      getData();
    }
  };

  const handleChangeSearch = async (e: any) => {
    setSearchInput(e.target.value);
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...params,
      page_no: 1,
      search_text: e.target.value,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    if (!e.target.value) {
      getData();
    }
  };
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  let payload = {
    loc_page_no: getValue(params, `page_no`, "1"),
    loc_search_text: getValue(params, `search_text`, ""),
  };
  let queryRequest = QueryRequestHelper(payload);

   /* -------------------------------------------------------------------------- */
  /*                              Date Routes                                   */
  /* -------------------------------------------------------------------------- */
  const [from_date, setFirstDay] = React.useState(new Date());
  const [to_date, setLastDay] = React.useState(new Date());
  const handleDate = (date: any) => {
    setFirstDay(date[0]);
    setLastDay(date[1]);
  };

  const datePickerRef = useRef<any>(null); // Specify the type explicitly

  const handleSubmitDate = () => {
    let first = convertDate(from_date);
    let last = convertDate(to_date);
    if (first && last && first !== "Invalid date" && last !== "Invalid date") {
      let payload: any = {
        ...params,
        from_date: first,
        to_date: last,
      };
      if (payload.from_date && payload.to_date) {
        let queryRequest = QueryRequestHelper(payload);
        navigate(`${window.location.pathname}?${queryRequest}`);
        if (datePickerRef.current) {
          datePickerRef.current.setOpen(false);
        }
      } else {
        toast.error("Start Date and End Date Required");
      }
    } else {
      toast.error("Please select date");
    }
  };

  const resetDate = () => {
    delete params.from_date;
    delete params.to_date;
    let payload: any = {
      ...params,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    let dates = new Date();
    var from_date = new Date(dates.getFullYear(), dates.getMonth(), 1);
    var to_date = new Date(dates.getFullYear(), dates.getMonth() + 1, 0);
    setFirstDay(from_date);
    setLastDay(to_date);
  };

  const handleCancelDate = () => {
    datePickerRef.current.setOpen(false);
    resetDate();
    getData();
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Form",
        accessor: "form",
        width: 150,
      },
      {
        Header: "Description",
        accessor: "description",
        width: 250,
      },
      {
        Header: "Legislation",
        accessor: "legislation",
        width: 150,
      },
      {
        Header: "Rule",
        accessor: "rule",
        width: 150,
      },
      {
        Header: "Added on",
        accessor: "created_at",
        width: 200,
      },
      {
        Header: "Current Document",
        width: 200,
        Cell: ({ row }: any) => {
            return (
              <>
                {getValue(row, `original.current_documents`, {}).length > 0 ? (
                  <a
                    onClick={() =>
                      handleCurrentDocument(getValue(row, `original`, {}))
                    }
                  >
                    View More
                  </a>
                ) : (
                  ""
                )}
              </>
            );
        },
      },
      
    {
        Header: "Previous Document",
        width: 200,
        Cell: ({ row }: any) => {
          return (
            <>
              {getValue(row, `original.previous_documents`, {}).length > 0 ? (
                <a
                  onClick={() =>
                    handlePreviousDocument(getValue(row, `original`, {}))
                  }
                >
                  View More
                </a>
              ) : (
                ""
              )}
            </>
          );
        },
      }
    ],
    []
  );
  const [selectedIDs, setSelectedIDs] = useState<any>([]);
  const getTableState = (value: object) => {
    let selectedIndexList = Object.keys(
      getValue(value, `selectedRowIds`, {})
    ).map((item) => parseInt(item));
    let selectedIds =
      documentLibraryList.length > 0
        ? documentLibraryList.filter((_item: object, index: number) =>
            selectedIndexList.includes(index)
          )
        : [];
    if (selectedIds.length > 0) {
      setSelectedIDs(
        selectedIds.map((item: object) => getValue(item, `id`, ""))
      );
    } else {
      setSelectedIDs([]);
    }
  };
  const rowClick = (obj: object) => {};
  const [check, setCheck] = useState(false);
  const handleRemoveNavItems = () => {
    setCheck(!check);
    setSelectedIDs([]);
  };
  const getTrProps = (rowInfo:any) => {
    if (getValue(rowInfo,`id`,'')) {
      return {
        style: {
        //   background:  'red',
        //   color: 'black'
        }
      }
    }
    return {};
  }

  const [currentDoc, setCurrentDoc] = useState("");
  const [currentDocIsOpen, setCurrentDocIsOpen] = useState(false);

  const handleCurrentDocPopup = () => {
    setCurrentDocIsOpen(!currentDocIsOpen);
  };

  const handleCurrentDocument = async (obj: object) => {
    setCurrentDoc(getValue(obj, `current_documents`, []));
    handleCurrentDocPopup();
  };


  const [previousDoc, setPreviousDoc] = useState("");
  const [previousDocIsOpen, setPreviousDocIsOpen] = useState(false);

  const handlePreviousDocPopup = () => {
    setPreviousDocIsOpen(!previousDocIsOpen);
  };

  const handlePreviousDocument = async (obj: object) => {
    setPreviousDoc(getValue(obj, `previous_documents`, []));
    handlePreviousDocPopup();
  };

  return(
    <DashboardLayout
      permissions={getValue(props, `tabPermissionList`, [])}
      subTabPermissionList={getValue(props, `subTabPermissionList`, [])}
    >
      <>
        <div className="dashboard-wrapper__header d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div className="header-parent-icon-holder">
              <MenuRepositorySvgComponent />
            </div>

            <div className="title-wrapper">
              <h6 className="dashbaord-wrapper__header-parent-title">Repository</h6>
              <h4 className="dashbaord-wrapper__header-title">Document Library</h4>
            </div>
          </div>
          <div className="d-flex align-items-center">
          
            <RightMenuHeaders
              //search
              isSearch={true}
              searchInput={search_text}
              showSearchTextbox={showSearchTextbox}
              toggleSearch={toggleSearch}
              toggleSearchClose={toggleSearchClose}
              handleChangeSearch={handleChangeSearch}
              //Line Seperator of button
              lineSeperator={true}
            />
          </div>
        </div>

        {!isLoading &&
        !getValue(props, `subTabPermissionList`, []).includes(
          "repository_document_library"
        ) ? (
          <NoAccessPage />
        ) : (
          <section>
            {isLoading || !props.permissionAPITriggered ? (
              <Loader />
            ) : getValue(documentLibraryList, `length`, 0) > 0 ? (
              <>
                <div className={`main-page-layout-wrapper`}>
                  <ScrollableDataTable
                    columns={columns}
                    data={documentLibraryList}
                    getTableState={getTableState}
                    rowClick={rowClick}
                    check={check}
                    selectedIDs={selectedIDs}
                    checkboxlogo
                    getTrProps={getTrProps}
                  />
                  <FooterWrapper
                    list={documentLibraryList}
                    totalCount={totalCount}
                    limit={limit}
                    page_no={page_no}
                    handleChangePagination={handleChangePagination}
                    handleChangeLimit={handleChangeLimit}
                  />
                </div>
                <DocumentLibraryPopup
                  currentDoc={currentDoc}
                  currentDocIsOpen={currentDocIsOpen}
                  handleCurrentDocPopup={handleCurrentDocPopup}
                />

                <PreviousDocumentLibraryPopup
                  previousDoc={previousDoc}
                  previousDocIsOpen={previousDocIsOpen}
                  handlePreviousDocPopup={handlePreviousDocPopup}
                />
              </>
            ) : (
              <>
                {Object.keys(params).length !== 0 ? <Notfound /> : <Notfound />}
              </>
            )}
          </section>
        )}
      </>
    </DashboardLayout>
  )

}

export default DocumentLibrary;
